import React from 'react'
import { createRoot } from 'react-dom/client'
import reportWebVitals from 'reportWebVitals'
import App from 'components/App'
import CssBaseline from '@mui/material/CssBaseline'
import { LicenseInfo } from '@mui/x-license-pro'
import smartlookClient from 'smartlook-client'
import * as FullStory from '@fullstory/browser'
import axios from 'axios'
import { API } from 'app-constants'
import { initFeatures } from 'hooks/use-flag'
import AppProvider from 'components/App/AppProvider'
import Bugsnag, { OnErrorCallback } from '@bugsnag/js'
import BugsnagPluginReact, { BugsnagErrorBoundary } from '@bugsnag/plugin-react'
import TagManager from 'react-gtm-module'
import { BrowserAgent } from '@newrelic/browser-agent/loaders/browser-agent'

type WindowWithDataLayer = Window & {
  dataLayer: Record<string, any>[]
}

declare const window: WindowWithDataLayer

axios.defaults.baseURL = API.ORIGIN

const BUGSNAG_KEY = process.env.REACT_APP_BUGSNAG_API_KEY
let ErrorBoundary: BugsnagErrorBoundary | undefined
if (BUGSNAG_KEY) {
  Bugsnag.start({
    apiKey: BUGSNAG_KEY,
    plugins: [new BugsnagPluginReact()],
    appVersion: process.env.REACT_APP_TAG,
  })

  ErrorBoundary = Bugsnag.getPlugin('react')!.createErrorBoundary(React)
}

function JelledErrorBoundary(props: {
  children?: React.ReactNode | undefined
  onError?: OnErrorCallback
  FallbackComponent?: React.ComponentType<{
    error: Error
    info: React.ErrorInfo
    clearError: () => void
  }>
}) {
  if (ErrorBoundary) {
    return <ErrorBoundary {...props} />
  }

  return <>{props.children}</>
}

const FULLSTORY_KEY = process.env.REACT_APP_FULLSTORY_KEY
const SMARTLOOK_KEY = process.env.REACT_APP_SMARTLOOK_KEY

// License for MUI Pro
process.env.REACT_APP_MUI_LICENSE_KEY
  ? LicenseInfo.setLicenseKey(process.env.REACT_APP_MUI_LICENSE_KEY)
  : console.error('missing MUI license key')

if (import.meta.env.MODE === 'development' && process.env.REACT_APP_MOCK_API === 'true') {
  // To use the mock orval api, run `npx msw init public/ --save` and
  // add the `REACT_APP_MOCK_API=true` env variable.

  const { setupWorker } = require('msw')
  const { getJelledServicesMSW } = require('api/endpoints/jelledFromFileSpecWithTransformer.msw.ts')

  const worker = setupWorker(...getJelledServicesMSW())
  worker.start()
}

const enableFs =
  (FULLSTORY_KEY && process.env.REACT_APP_ENABLE_FULLSTORY === 'true' && import.meta.env.MODE !== 'development') ||
  false
if (enableFs && FULLSTORY_KEY) {
  FullStory.init({ orgId: FULLSTORY_KEY })
}

const enableSl =
  (SMARTLOOK_KEY && process.env.REACT_APP_ENABLE_SMARTLOOK === 'true' && import.meta.env.MODE !== 'development') ||
  false
if (enableSl && SMARTLOOK_KEY) {
  smartlookClient.init(SMARTLOOK_KEY)
}

// New Relic Browser Agent
if (import.meta.env.MODE === 'production') {
  const options = {
    init: {
      distributed_tracing: { enabled: true },
      privacy: { cookies_enabled: true },
      ajax: { deny_list: ['bam.nr-data.net'] },
    },
    info: {
      beacon: 'bam.nr-data.net',
      errorBeacon: 'bam.nr-data.net',
      licenseKey: 'NRJS-db1872516287d119cca',
      applicationID: '1588923913',
      sa: 1,
    },
    loader_config: {
      accountID: '3490706',
      trustKey: '3490706',
      agentID: '1588923913',
      licenseKey: 'NRJS-db1872516287d119cca',
      applicationID: '1588923913',
    },
  }
  new BrowserAgent(options)
}

const initializeGTM = () => {
  const tagManagerArgs = {
    gtmId: 'G-07X6BDPEFR', // GTM ID
  }
  TagManager.initialize(tagManagerArgs)

  // Google Analytics and Google Ads setup
  window.dataLayer = window.dataLayer || []
  function gtag(...args: any[]) {
    window.dataLayer.push(args)
  }
  gtag('js', new Date())
  gtag('config', 'G-07X6BDPEFR')
  gtag('config', 'AW-16510978365')
}

if (process.env.NODE_ENV === 'production') {
  initializeGTM()
}

initFeatures()

const root = createRoot(document.getElementById('root')!)
root.render(
  <JelledErrorBoundary>
    <AppProvider>
      <CssBaseline />
      <App />
    </AppProvider>
  </JelledErrorBoundary>
)

// If you want to start measuring performance in your app, pass a function
// to log results (for example: reportWebVitals(console.log))
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
reportWebVitals()
